/**
 * An rc-picker `GenerateConfig<Date>` backed by dayjs.
 *
 * The codebase historically built its pickers with `generatePicker<Date>` +
 * `rc-picker/es/generate/dateFns`, so their surrounding form/state code is all
 * typed on native `Date`. To drop date-fns without rewriting that Date flow, we
 * wrap rc-picker's dayjs config and convert `Date <-> Dayjs` at each boundary.
 * Pickers built with this config keep their `Date` value/onChange API while
 * dayjs does all the date math and formatting (so their `format` props use
 * dayjs tokens, e.g. `DD/MM/YYYY`).
 */
import type { GenerateConfig } from 'rc-picker/es/generate';
import dayjsGenerateConfig from 'rc-picker/es/generate/dayjs';

// Ensure our dayjs copy (with all plugins) is the one used for conversions.
import { dayjs } from 'app/utils/dayjs';

const toDate = (value: { toDate: () => Date } | null): Date | null =>
  value ? value.toDate() : null;

export const dateGenerateConfig: GenerateConfig<Date> = {
  getNow: () => dayjsGenerateConfig.getNow().toDate(),
  getFixedDate: (fixed) => dayjsGenerateConfig.getFixedDate(fixed).toDate(),
  getEndDate: (value) => dayjsGenerateConfig.getEndDate(dayjs(value)).toDate(),
  getWeekDay: (value) => dayjsGenerateConfig.getWeekDay(dayjs(value)),
  getYear: (value) => dayjsGenerateConfig.getYear(dayjs(value)),
  getMonth: (value) => dayjsGenerateConfig.getMonth(dayjs(value)),
  getDate: (value) => dayjsGenerateConfig.getDate(dayjs(value)),
  getHour: (value) => dayjsGenerateConfig.getHour(dayjs(value)),
  getMinute: (value) => dayjsGenerateConfig.getMinute(dayjs(value)),
  getSecond: (value) => dayjsGenerateConfig.getSecond(dayjs(value)),
  addYear: (value, diff) =>
    dayjsGenerateConfig.addYear(dayjs(value), diff).toDate(),
  addMonth: (value, diff) =>
    dayjsGenerateConfig.addMonth(dayjs(value), diff).toDate(),
  addDate: (value, diff) =>
    dayjsGenerateConfig.addDate(dayjs(value), diff).toDate(),
  setYear: (value, year) =>
    dayjsGenerateConfig.setYear(dayjs(value), year).toDate(),
  setMonth: (value, month) =>
    dayjsGenerateConfig.setMonth(dayjs(value), month).toDate(),
  setDate: (value, date) =>
    dayjsGenerateConfig.setDate(dayjs(value), date).toDate(),
  setHour: (value, hour) =>
    dayjsGenerateConfig.setHour(dayjs(value), hour).toDate(),
  setMinute: (value, minute) =>
    dayjsGenerateConfig.setMinute(dayjs(value), minute).toDate(),
  setSecond: (value, second) =>
    dayjsGenerateConfig.setSecond(dayjs(value), second).toDate(),
  isAfter: (date1, date2) =>
    dayjsGenerateConfig.isAfter(dayjs(date1), dayjs(date2)),
  isValidate: (value) => dayjsGenerateConfig.isValidate(dayjs(value)),
  locale: {
    getWeekFirstDay: (locale) =>
      dayjsGenerateConfig.locale.getWeekFirstDay(locale),
    getWeekFirstDate: (locale, value) =>
      dayjsGenerateConfig.locale
        .getWeekFirstDate(locale, dayjs(value))
        .toDate(),
    getWeek: (locale, value) =>
      dayjsGenerateConfig.locale.getWeek(locale, dayjs(value)),
    format: (locale, date, formatStr) =>
      dayjsGenerateConfig.locale.format(locale, dayjs(date), formatStr),
    parse: (locale, text, formats) =>
      toDate(dayjsGenerateConfig.locale.parse(locale, text, formats)),
    getShortWeekDays: (locale) =>
      dayjsGenerateConfig.locale.getShortWeekDays?.(locale) ?? [],
    getShortMonths: (locale) =>
      dayjsGenerateConfig.locale.getShortMonths?.(locale) ?? [],
  },
};
